import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";

import api from "../../services/api/axios";
import BrandSpinner from "../../shared/components/BrandSpinner";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { formatDateTime } from "../../utils/formatters";
import {
  createNotification,
  deactivateNotification,
  fetchNotifications,
} from "./notificationService";

import NotificationVideo from "./NotificationVideo";

const emptyVideoFields = {
  video_url: "", video_title: "", video_thumbnail: "", video_cta_text: "",
};
const videoFields = [
  ["video_url", "Tutorial video URL", "Paste the YouTube video link. Opens in a new tab."],
  ["video_title", "Tutorial title", "Defaults to the notification title."],
  ["video_cta_text", "Tutorial button text", "Defaults to Watch Tutorial."],
];

const severityOptions = [
  { value: "info", label: "Info", color: BRAND_BLUE, icon: InfoOutlinedIcon },
  { value: "success", label: "Success", color: BRAND_GREEN, icon: CheckCircleOutlineRoundedIcon },
  { value: "warning", label: "Warning", color: "#b26a00", icon: WarningAmberRoundedIcon },
  { value: "error", label: "Error", color: "#c62828", icon: ErrorOutlineRoundedIcon },
];
const ADMIN_NOTIFICATION_TIMESTAMP_OPTIONS = {
  fallback: "No expiration",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const formatTimestamp = (value) =>
  formatDateTime(value, ADMIN_NOTIFICATION_TIMESTAMP_OPTIONS);

function NotificationPreview({ draft }) {
  const config =
    severityOptions.find((option) => option.value === draft.severity) ||
    severityOptions[0];
  const Icon = config.icon;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${config.color}33`,
        background: `linear-gradient(180deg, ${config.color}10 0%, rgba(255,255,255,0.96) 100%)`,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              mt: 0.2,
              width: 42,
              height: 42,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              backgroundColor: `${config.color}18`,
              color: config.color,
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
              <Typography sx={{ fontWeight: 800, color: BRAND_BLUE }}>
                {draft.title || "Notification title preview"}
              </Typography>
              {draft.is_banner && (
                <Chip
                  label="Banner"
                  size="small"
                  sx={{ color: BRAND_BLUE, backgroundColor: `${BRAND_BLUE}12`, fontWeight: 700 }}
                />
              )}
              <Chip
                label={draft.send_to_all_orgs ? "All organizations" : "Targeted organization"}
                size="small"
                sx={{ color: config.color, backgroundColor: `${config.color}10`, fontWeight: 700 }}
              />
            </Stack>

            <Typography
              variant="body2"
              sx={{ color: "text.primary", whiteSpace: "pre-line", mb: 1.25 }}
            >
              {draft.message || "Your message preview will appear here."}
            </Typography>

            <NotificationVideo notification={draft} />

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {draft.expires_at ? `Expires ${formatTimestamp(draft.expires_at)}` : "No expiration set"}
              </Typography>
              {draft.link_url && (
                <Chip
                  label={draft.link_url}
                  size="small"
                  sx={{ maxWidth: "100%", "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function AdminNotifications() {
  const [draft, setDraft] = useState({
    ...emptyVideoFields,
    title: "",
    message: "",
    severity: "info",
    send_to_all_orgs: true,
    organization_id: "",
    expires_at: null,
    link_url: "",
    is_banner: false,
  });
  const [notifications, setNotifications] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadPageData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [notificationsData, organizationsResponse] = await Promise.all([
        fetchNotifications({
          admin_view: true,
          include_expired: true,
          limit: 100,
        }),
        api.get("/admin/organizations"),
      ]);

      setNotifications(notificationsData);
      setOrganizations(organizationsResponse.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to load admin notifications.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const previewDraft = useMemo(
    () => ({
      ...draft,
      expires_at: draft.expires_at ? draft.expires_at.toISOString() : null,
    }),
    [draft],
  );

  const handleCreate = useCallback(async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        title: draft.title.trim(),
        message: draft.message.trim(),
        severity: draft.severity,
        send_to_all_orgs: draft.send_to_all_orgs,
        organization_id:
          draft.send_to_all_orgs || !draft.organization_id
            ? null
            : Number(draft.organization_id),
        expires_at: draft.expires_at ? draft.expires_at.toISOString() : null,
        link_url: draft.link_url.trim() || null,
        is_banner: draft.is_banner,
        ...Object.fromEntries(videoFields.map(([key]) => [
          key, draft.video_url.trim() ? draft[key].trim() || null : null,
        ])),
      };

      const created = await createNotification(payload);
      setNotifications((prev) => [created, ...prev]);
      setDraft({
        ...emptyVideoFields,
        title: "",
        message: "",
        severity: "info",
        send_to_all_orgs: true,
        organization_id: "",
        expires_at: null,
        link_url: "",
        is_banner: false,
      });
      setSuccess("Notification sent successfully.");
    } catch (err) {
      setError(
        (Array.isArray(err?.response?.data?.detail)
          ? err.response.data.detail.map((item) => item.msg).join("; ")
          : err?.response?.data?.detail) ||
          err?.message ||
          "Unable to create notification.",
      );
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const handleDeactivate = useCallback(async (notificationId) => {
    try {
      setDeactivatingId(notificationId);
      setError("");
      setSuccess("");
      await deactivateNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((item) => item.id !== notificationId),
      );
      setSuccess("Notification deactivated.");
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to deactivate notification.",
      );
    } finally {
      setDeactivatingId(null);
    }
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", lg: "center" }}
          spacing={1.5}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
              Admin Notifications
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              Publish product announcements, maintenance notices, onboarding tips, and outage updates to every user in-app.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<NotificationsActiveRoundedIcon />}
            onClick={loadPageData}
            disabled={loading}
            sx={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
          >
            Refresh
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2.5 }}>{success}</Alert>}

        <Stack direction={{ xs: "column", xl: "row" }} spacing={3} alignItems="stretch">
          <Card
            elevation={4}
            sx={{
              flex: 1.05,
              borderRadius: 4,
              border: `1px solid ${BRAND_BLUE}22`,
              boxShadow: "0 18px 40px rgba(0, 36, 77, 0.08)",
            }}
          >
            <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, mb: 2 }}>
                Create Notification
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label="Title"
                  value={draft.title}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, title: event.target.value }))
                  }
                  fullWidth
                />

                <TextField
                  label="Message"
                  value={draft.message}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, message: event.target.value }))
                  }
                  multiline
                  minRows={4}
                  fullWidth
                />

                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel id="notification-severity-label">Severity</InputLabel>
                    <Select
                      labelId="notification-severity-label"
                      label="Severity"
                      value={draft.severity}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, severity: event.target.value }))
                      }
                    >
                      {severityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DateTimePicker
                    label="Expiration"
                    value={draft.expires_at}
                    onChange={(value) =>
                      setDraft((prev) => ({
                        ...prev,
                        expires_at: value && dayjs(value).isValid() ? dayjs(value) : null,
                      }))
                    }
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Stack>

                <TextField
                  label="Optional CTA link"
                  value={draft.link_url}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, link_url: event.target.value }))
                  }
                  placeholder="https://status.example.com or /reports/builder"
                  fullWidth
                />

                <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
                  Optional tutorial video
                </Typography>
                {videoFields.filter(([key]) => key === "video_url" || draft.video_url).map(([key, label, helperText]) => (
                  <TextField
                    key={key}
                    label={label}
                    helperText={helperText}
                    value={draft[key]}
                    onChange={(event) => setDraft((prev) => ({ ...prev, [key]: event.target.value }))}
                    fullWidth
                  />
                ))}

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  sx={{ alignItems: { md: "center" } }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.send_to_all_orgs}
                        onChange={(event) =>
                          setDraft((prev) => ({
                            ...prev,
                            send_to_all_orgs: event.target.checked,
                            organization_id: event.target.checked ? "" : prev.organization_id,
                          }))
                        }
                      />
                    }
                    label="Send to all organizations"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.is_banner}
                        onChange={(event) =>
                          setDraft((prev) => ({ ...prev, is_banner: event.target.checked }))
                        }
                      />
                    }
                    label="Flag as banner"
                  />
                </Stack>

                {!draft.send_to_all_orgs && (
                  <FormControl fullWidth>
                    <InputLabel id="notification-org-label">Organization</InputLabel>
                    <Select
                      labelId="notification-org-label"
                      label="Organization"
                      value={draft.organization_id}
                      onChange={(event) =>
                        setDraft((prev) => ({ ...prev, organization_id: event.target.value }))
                      }
                    >
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Divider sx={{ my: 0.5 }} />

                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PreviewRoundedIcon sx={{ color: BRAND_GREEN }} />
                    <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
                      Preview
                    </Typography>
                  </Stack>
                  <NotificationPreview draft={previewDraft} />
                </Stack>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={saving}
                    sx={{
                      px: 2.5,
                      py: 1.1,
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, ${BRAND_GREEN} 100%)`,
                    }}
                  >
                    {saving ? "Sending..." : "Send Notification"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={4}
            sx={{
              flex: 0.95,
              minWidth: 0,
              borderRadius: 4,
              border: `1px solid ${BRAND_BLUE}22`,
              boxShadow: "0 18px 40px rgba(0, 36, 77, 0.08)",
            }}
          >
            <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
              <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, mb: 2 }}>
                Previously Sent
              </Typography>

              {loading ? (
                <BrandSpinner compact label="Loading notifications…" py={6} />
              ) : notifications.length < 1 ? (
                <Alert severity="info">No notifications have been sent yet.</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {notifications.map((notification) => {
                    const config =
                      severityOptions.find((option) => option.value === notification.severity) ||
                      severityOptions[0];
                    const Icon = config.icon;

                    return (
                      <Card
                        key={notification.id}
                        elevation={0}
                        sx={{
                          borderRadius: 3,
                          border: `1px solid ${config.color}25`,
                          backgroundColor: notification.is_active ? "#fff" : "#f6f7f8",
                          opacity: notification.is_active ? 1 : 0.78,
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Box
                              sx={{
                                mt: 0.2,
                                width: 38,
                                height: 38,
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                backgroundColor: `${config.color}16`,
                                color: config.color,
                                flexShrink: 0,
                              }}
                            >
                              <Icon fontSize="small" />
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1.25}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
                                    {notification.title}
                                  </Typography>
                                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
                                    <Chip size="small" label={config.label} sx={{ backgroundColor: `${config.color}10`, color: config.color, fontWeight: 700 }} />
                                    <Chip size="small" label={notification.send_to_all_orgs ? "All orgs" : notification.organization_name || "Targeted"} sx={{ fontWeight: 700 }} />
                                    {notification.is_banner && <Chip size="small" label="Banner" sx={{ backgroundColor: `${BRAND_BLUE}10`, color: BRAND_BLUE, fontWeight: 700 }} />}
                                    <Chip
                                      size="small"
                                      label={notification.is_active ? "Active" : "Inactive"}
                                      sx={{
                                        backgroundColor: notification.is_active ? `${BRAND_GREEN}12` : "rgba(0,0,0,0.06)",
                                        color: notification.is_active ? BRAND_GREEN : "text.secondary",
                                        fontWeight: 700,
                                      }}
                                    />
                                  </Stack>
                                </Box>

                                <Button
                                  color="error"
                                  variant="text"
                                  startIcon={<DeleteOutlineRoundedIcon />}
                                  disabled={!notification.is_active || deactivatingId === notification.id}
                                  onClick={() => handleDeactivate(notification.id)}
                                  sx={{ flexShrink: 0 }}
                                >
                                  {deactivatingId === notification.id ? "Removing..." : "Deactivate"}
                                </Button>
                              </Stack>

                              <Typography
                                variant="body2"
                                sx={{ mt: 1.2, color: "text.primary", whiteSpace: "pre-line" }}
                              >
                                {notification.message}
                              </Typography>

                              <NotificationVideo notification={notification} />

                              <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 1.25 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  Sent {formatTimestamp(notification.created_at)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                  Expires {formatTimestamp(notification.expires_at)}
                                </Typography>
                                {notification.link_url && (
                                  <Typography variant="caption" sx={{ color: BRAND_BLUE }}>
                                    CTA: {notification.link_url}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
}
